import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { Inbox } from 'lucide-react';
import { Button } from './Button';

export interface EmptyStateArt {
  src: string;
  /** Intrinsic pixels — reserved up front so the art never shifts the layout. */
  width: number;
  height: number;
  /** Rendered CSS width. Height follows the intrinsic ratio. */
  displayWidth?: number;
}

export interface EmptyStateProps {
  icon?: ReactNode;
  /**
   * A pre-rendered 3D vignette for first-run moments — the one place the
   * product gets to be pictorial. Decorative by definition (empty alt), lazy,
   * and it replaces the icon rather than stacking with it.
   */
  art?: EmptyStateArt;
  /** What is not here. A statement, not an apology. */
  headline: string;
  /** What to do about it. One sentence. */
  description?: ReactNode;
  /** Exactly one action — an empty state with three choices is a menu. */
  action?: { label: string; onClick: () => void; variant?: 'primary' | 'secondary' };
  size?: 'sm' | 'md';
  className?: string;
}

export function EmptyState({
  icon,
  art,
  headline,
  description,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        size === 'sm' ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
        className,
      )}
    >
      {art ? (
        <img
          src={art.src}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          width={art.width}
          height={art.height}
          className="rounded-lg shadow-card"
          style={{ width: art.displayWidth ?? 240, height: 'auto' }}
        />
      ) : (
        <span
          aria-hidden
          className={clsx(
            'flex items-center justify-center rounded-full bg-surface-2 text-faint',
            size === 'sm' ? 'h-9 w-9 [&>svg]:h-4 [&>svg]:w-4' : 'h-12 w-12 [&>svg]:h-5 [&>svg]:w-5',
          )}
        >
          {icon ?? <Inbox />}
        </span>
      )}

      <div className="max-w-sm">
        <p
          className={clsx(
            'font-display font-semibold text-ink',
            size === 'sm' ? 'text-body' : 'text-body',
          )}
        >
          {headline}
        </p>
        {description ? (
          <p className="mt-1 text-micro leading-relaxed text-muted">{description}</p>
        ) : null}
      </div>

      {action ? (
        <Button
          variant={action.variant ?? 'secondary'}
          size={size === 'sm' ? 'sm' : 'md'}
          onClick={action.onClick}
          className="mt-1"
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
